/**
 * Compliance framework display names and utilities.
 * Single source of truth shared by server and web packages.
 */

import type { ComplianceFramework as ComplianceFrameworkValue } from './generated/enums';

export type ComplianceFramework = ComplianceFrameworkValue;

/**
 * Enum-like const object mirroring the `ComplianceFrameworkSchema` Zod
 * enum. Generated enums from Zod are inferred as string-literal unions,
 * so callers that want `ComplianceFramework.SOC2` dot-access import this
 * const (which satisfies the enum type and is guaranteed in sync via the
 * `satisfies Record<ComplianceFramework, ...>` check).
 */
export const ComplianceFramework = {
  SOC2: 'SOC2',
  HIPAA: 'HIPAA',
  CMMC: 'CMMC',
  ISO27001: 'ISO27001',
  ISO42001: 'ISO42001',
  GDPR: 'GDPR',
  HITRUST: 'HITRUST',
  FEDRAMP: 'FEDRAMP',
  NIST_800_53: 'NIST_800_53',
  NIST_800_171: 'NIST_800_171',
  PCI_DSS: 'PCI_DSS',
} as const satisfies Record<string, ComplianceFramework>;

/**
 * Evidence-guidance legal disclaimer (market-standard; Drata publishes the
 * same notice above its per-framework example-evidence guidance).
 *
 * We tell customers which artifacts satisfy which requirement, and for a
 * regulation — GDPR, HIPAA — that guidance reads as a legal conclusion unless
 * it says otherwise. It is not one: a requirement's application turns on facts
 * we do not hold (processing purposes, establishment, volumes, the customer's
 * own contracts). One constant, imported by every surface, so the wording
 * cannot drift between the app, the exports, and the generated reports.
 *
 * Placement rule: show it wherever we RECOMMEND evidence, not merely wherever
 * a framework is named.
 */
export const EVIDENCE_GUIDANCE_LEGAL_DISCLAIMER =
  'This is not legal advice. Have your legal team review evidence before you rely on it, to confirm each document fits your organization’s specific facts and circumstances. We cannot guarantee that the guidance here satisfies every requirement that applies to you.';

/** The same notice, naming the framework — for a single-framework surface. */
export const evidenceGuidanceLegalDisclaimer = (
  framework: ComplianceFramework | string,
): string =>
  `This is not legal advice. Have your legal team review evidence before you rely on it, to confirm each document fits your organization’s specific facts and circumstances. We cannot guarantee that the guidance here satisfies the specific requirements of ${
    FRAMEWORK_DISPLAY_NAMES[framework as ComplianceFramework] ?? framework
  } as they apply to you.`;

export const FRAMEWORK_DISPLAY_NAMES: Record<ComplianceFramework, string> = {
  SOC2: 'SOC 2',
  HIPAA: 'HIPAA',
  CMMC: 'CMMC',
  ISO27001: 'ISO 27001',
  ISO42001: 'ISO 42001',
  GDPR: 'GDPR',
  HITRUST: 'HITRUST',
  FEDRAMP: 'FedRAMP',
  NIST_800_53: 'NIST SP 800-53',
  NIST_800_171: 'NIST SP 800-171',
  PCI_DSS: 'PCI DSS',
};

export function getFrameworkDisplayName(framework: string): string {
  return (FRAMEWORK_DISPLAY_NAMES as Record<string, string>)[framework] ?? framework;
}

// ══════════════════════════════════════════════════════════════════════
// Onboarding enrollment vocabulary
// ══════════════════════════════════════════════════════════════════════

/**
 * Framework keys the onboarding wizard can represent end-to-end — the
 * checkbox options, the client answer mapper, the draft-save schema, the
 * final-save schema, and the enrolled-framework hydration all derive from
 * this ONE list.
 *
 * It exists because those five spots each carried their own hardcoded copy,
 * and ISO 42001 was added to the framework layer (RFC-216 M3) without any of
 * them being widened. The failure was not cosmetic: onboarding save is
 * AUTHORITATIVE for enrollment, so a key the wizard could not represent was
 * hydrated as `null`, dropped from the submitted list, and then DELETED from
 * a workspace that had been provisioned with it. A framework contracted at
 * sale vanished the first time the customer finished the wizard.
 *
 * Must stay in sync with the server's `getSupportedFrameworks()`; a test
 * asserts the two agree, so adding framework #5 fails loudly here rather
 * than silently un-enrolling it in production.
 */
export const ONBOARDING_FRAMEWORK_KEYS = [
  'SOC2',
  'HIPAA',
  'ISO27001',
  'ISO42001',
  'GDPR',
] as const;

export type OnboardingFrameworkKey = (typeof ONBOARDING_FRAMEWORK_KEYS)[number];

/**
 * The above plus `OTHER`, the catch-all the wizard offers for frameworks we
 * don't model (PCI DSS, FedRAMP, …). `OTHER` is a real enrollment value but
 * not a `ComplianceFramework` enum member, so it never reaches
 * `Workspace.enabledFrameworks`.
 */
export const ONBOARDING_ENROLLMENT_KEYS = [
  ...ONBOARDING_FRAMEWORK_KEYS,
  'OTHER',
] as const;

export type OnboardingEnrollmentKey =
  (typeof ONBOARDING_ENROLLMENT_KEYS)[number];

/** Zod-friendly tuple form (`z.enum` needs a non-empty literal tuple). */
export const ONBOARDING_ENROLLMENT_KEY_TUPLE = ONBOARDING_ENROLLMENT_KEYS as unknown as [
  OnboardingEnrollmentKey,
  ...OnboardingEnrollmentKey[],
];

/**
 * True when onboarding can carry this framework key through a save without
 * losing it. Anything else must be PRESERVED by the authoritative enrollment
 * reconcile rather than deleted — the wizard's silence about a framework is
 * not the user asking to un-enroll it.
 */
export function isOnboardingEnrollmentKey(
  framework: string,
): framework is OnboardingEnrollmentKey {
  return (ONBOARDING_ENROLLMENT_KEYS as readonly string[]).includes(framework);
}

/**
 * Frameworks whose enrollment is gated behind a feature flag → the flag key.
 * Shared so the server gate, the provisioning scripts, and the onboarding UI
 * all read one table; each previously kept its own annotated copy.
 */
export const FRAMEWORK_ENROLLMENT_FLAGS: Record<string, string> = {
  ISO27001: 'iso27001',
  ISO42001: 'iso42001',
  // RFC-222 §8: internal-only until the D9 review artifact passes and the gap
  // inventory is published (D9 accepted 2026-08-22; flag stays per-org opt-in).
  GDPR: 'gdpr',
};

// ══════════════════════════════════════════════════════════════════════
// GDPR enrollment scope vocabulary (RFC-222 §6, M2)
// ══════════════════════════════════════════════════════════════════════

/**
 * The organizational-role and territorial-basis tokens recorded on
 * `ComplianceProgramFramework.scope` for a GDPR enrollment. A COMPLETE
 * (active) GDPR enrollment requires at least one role and one basis;
 * unknown tokens are rejected server-side (`validateFrameworkEnrollmentScope`).
 * Multiple roles are allowed — one company can be a controller for workforce
 * and marketing data while acting as a processor for customer data.
 */
export const GDPR_ROLE_SCOPE_TOKENS = [
  'ROLE_CONTROLLER',
  'ROLE_PROCESSOR',
  'ROLE_JOINT_CONTROLLER',
] as const;

export const GDPR_BASIS_SCOPE_TOKENS = [
  'BASIS_EU_ESTABLISHMENT',
  'BASIS_OFFERING_TO_EU',
  'BASIS_MONITORING_EU',
] as const;

export const GDPR_SCOPE_TOKENS = [
  ...GDPR_ROLE_SCOPE_TOKENS,
  ...GDPR_BASIS_SCOPE_TOKENS,
] as const;

export type GdprScopeToken = (typeof GDPR_SCOPE_TOKENS)[number];
export type GdprRoleScopeToken = (typeof GDPR_ROLE_SCOPE_TOKENS)[number];
export type GdprBasisScopeToken = (typeof GDPR_BASIS_SCOPE_TOKENS)[number];

export const GDPR_SCOPE_TOKEN_LABELS: Record<GdprScopeToken, { label: string; description: string }> = {
  ROLE_CONTROLLER: {
    label: 'Controller',
    description: 'We decide why and how personal data is processed (our customers, marketing, employees).',
  },
  ROLE_PROCESSOR: {
    label: 'Processor',
    description: 'We process personal data on behalf of our customers (their end-users\u2019 data in our product).',
  },
  ROLE_JOINT_CONTROLLER: {
    label: 'Joint controller',
    description: 'We determine purposes and means of some processing together with another organization.',
  },
  BASIS_EU_ESTABLISHMENT: {
    label: 'EU establishment',
    description: 'We have an entity, office, or staff established in the EU/EEA.',
  },
  BASIS_OFFERING_TO_EU: {
    label: 'Offering to EU data subjects',
    description: 'We offer goods or services to people in the EU/EEA (paid or free).',
  },
  BASIS_MONITORING_EU: {
    label: 'Monitoring EU behaviour',
    description: 'We monitor the behaviour of people in the EU/EEA (analytics, tracking, profiling).',
  },
};

export function isGdprRoleScopeToken(token: string): token is GdprRoleScopeToken {
  return (GDPR_ROLE_SCOPE_TOKENS as readonly string[]).includes(token);
}
export function isGdprBasisScopeToken(token: string): token is GdprBasisScopeToken {
  return (GDPR_BASIS_SCOPE_TOKENS as readonly string[]).includes(token);
}

// ══════════════════════════════════════════════════════════════════════
// AuditCycle.auditType vocabulary
// ══════════════════════════════════════════════════════════════════════

/**
 * Every value `AuditCycle.auditType` may hold. The column is free-text
 * `String` in Prisma, so this const is the real contract — the auditor
 * router validates against it and every producer defaults from it.
 *
 * Four producers used to invent their own ISO 27001 vocabulary
 * independently (`STAGE_1` in the onboarding answer mapper,
 * `CERTIFICATION` in `decodeComplianceGoal`, `SELF_ASSESSMENT` /
 * `THIRD_PARTY_AUDIT` in the create-cycle dialog, and a fourth list in the
 * Prisma schema comment). Cycles written by one path were unreadable by
 * the others. ISO now reuses the same internal/external pair as HIPAA —
 * an ISO engagement genuinely is a third-party audit by an accredited
 * certification body, and the user-facing label carries the nuance
 * ("External certification"), not the stored key.
 */
export const AUDIT_TYPES = [
  'TYPE_I',
  'TYPE_II',
  'SELF_ASSESSMENT',
  'THIRD_PARTY_AUDIT',
] as const;

export type AuditType = (typeof AUDIT_TYPES)[number];

/** Audit types offered per framework, in display order. Index 0 is the default. */
export const AUDIT_TYPES_BY_FRAMEWORK: Record<string, readonly AuditType[]> = {
  // Type II is index 0, so an enrollment with no explicit answer defaults to
  // it — matching what onboarding recommends. A first-time SOC 2 program that
  // never answers the question is better off scoped for the report its buyers
  // actually ask for; Type I stays one click away.
  SOC2: ['TYPE_II', 'TYPE_I'],
  HIPAA: ['SELF_ASSESSMENT', 'THIRD_PARTY_AUDIT'],
  // Both ISO standards certify through an accredited body, so the external
  // engagement is the DEFAULT (index 0) rather than the alternative — an ISO
  // program that stops at self-assessment produces no certificate. Onboarding
  // asks no per-framework audit-type question for them precisely because the
  // answer is not in doubt; self-assessment stays selectable for teams running
  // an internal readiness pass before booking the certification audit.
  ISO27001: ['THIRD_PARTY_AUDIT', 'SELF_ASSESSMENT'],
  // Without an entry here `defaultAuditTypeForFramework` fell through to
  // TYPE_I, which would label an ISO 42001 cycle "Type I" — SOC 2 vocabulary
  // on a non-SOC 2 audit.
  ISO42001: ['THIRD_PARTY_AUDIT', 'SELF_ASSESSMENT'],
  // RFC-222 §7.1/D8: GDPR v1 is SELF_ASSESSMENT only — no certification or
  // Type I/II vocabulary, and no silent TYPE_I fallthrough (§8 hard rule).
  GDPR: ['SELF_ASSESSMENT'],
};

/**
 * Audit type to assign when a framework is enrolled without an explicit
 * choice (onboarding writes one AuditCycle per enrolled framework before
 * the user has picked an engagement type).
 */
export function defaultAuditTypeForFramework(framework: string): AuditType {
  return AUDIT_TYPES_BY_FRAMEWORK[framework]?.[0] ?? 'TYPE_I';
}

/**
 * Human label for an audit type. The retired ISO literals are included so
 * pre-unification rows still render as words rather than falling through to
 * the title-caser.
 */
const AUDIT_TYPE_LABELS: Record<string, string> = {
  TYPE_I: 'Type I',
  TYPE_II: 'Type II',
  SELF_ASSESSMENT: 'Internal readiness',
  THIRD_PARTY_AUDIT: 'External review',
  // Retired — see the AUDIT_TYPES doc comment.
  STAGE_1: 'Stage 1',
  STAGE_2: 'Stage 2',
  CERTIFICATION: 'Certification',
  THIRD_PARTY: 'External review',
};

/** Overrides where a framework calls the same engagement something else. */
const FRAMEWORK_AUDIT_TYPE_LABELS: Record<string, Record<string, string>> = {
  // An ISO external engagement produces a certificate, not a report.
  ISO27001: { THIRD_PARTY_AUDIT: 'External certification' },
  ISO42001: { THIRD_PARTY_AUDIT: 'External certification' },
};

/**
 * Wording for the PUBLIC trust center. Prospects and their security reviewers
 * read this page, so it uses the industry-standard terms rather than our
 * product voice — "Self-assessment" reads as a known compliance posture,
 * "Internal readiness" reads as marketing.
 */
const PUBLIC_AUDIT_TYPE_LABELS: Record<string, string> = {
  SELF_ASSESSMENT: 'Self-assessment',
  THIRD_PARTY_AUDIT: 'Third-party audit',
  THIRD_PARTY: 'Third-party audit',
};

/**
 * Single source of truth for rendering `AuditCycle.auditType` as text —
 * chips, cycle labels, the public trust center, and the create-cycle dialog
 * all route through here. Three formatters used to disagree (one produced
 * SHOUTY_UNDERSCORE labels, one lacked THIRD_PARTY_AUDIT entirely).
 *
 * `framework` is optional; pass it wherever it's known so ISO renders its
 * certification wording. Pass `{ audience: 'public' }` on the trust center,
 * where standard compliance terminology beats our product voice.
 */
export function getAuditTypeLabel(
  auditType: string | null | undefined,
  framework?: string | null,
  opts?: { audience?: 'internal' | 'public' },
): string {
  if (!auditType) return '';
  if (opts?.audience === 'public' && PUBLIC_AUDIT_TYPE_LABELS[auditType]) {
    return PUBLIC_AUDIT_TYPE_LABELS[auditType];
  }
  const override = framework
    ? FRAMEWORK_AUDIT_TYPE_LABELS[framework]?.[auditType]
    : undefined;
  if (override) return override;
  return (
    AUDIT_TYPE_LABELS[auditType] ??
    // Unknown value (hand-edited row, future framework): title-case it
    // rather than leaking SCREAMING_SNAKE to the user.
    auditType
      .toLowerCase()
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  );
}
